/**
 * The packages an admin may move a business onto: published, and from the business's OWN
 * catalog only — its reseller's, or the platform's for a direct business.
 *
 * The admin plan pickers ("No-charge override", "Upgrade & invoice") used to list every published
 * package from every catalog, unlabelled. On prod "Regular" showed three times at three catalogs'
 * prices and "Ultimate ৳12,000" three times identically (2026-09-12), so an admin could not tell
 * which one was the customer's. The API enforces the same rule.
 */
export function ownCatalogPackages<T extends { status: string; catalogResellerId?: string | null }>(
  packages: readonly T[],
  businessResellerId: string | null | undefined,
): T[] {
  const own = businessResellerId ?? null;
  return packages.filter((p) => p.status === 'published' && (p.catalogResellerId ?? null) === own);
}

/**
 * A plan a cash entry may START a subscription on, for a business with no live one — the
 * "Record payment" picker (2026-09-15). Prices are minor units; `null` = not sold in that interval.
 */
export type CashPlanChoice = {
  id: string;
  name: string;
  caption?: string | null;
  monthlyMinor: number | null;
  yearlyMinor: number | null;
};

/** Admin surfaces: the business's own catalog (lib rule above) joined with its ACTIVE prices. */
export function adminCashPlanChoices<
  P extends { id: string; name: string; status: string; tagline?: string; catalogResellerId?: string | null },
  Pr extends { packageId: string; interval: string; amount: number; status: string },
>(packages: readonly P[], prices: readonly Pr[], businessResellerId: string | null | undefined): CashPlanChoice[] {
  const active = (pkgId: string, iv: 'month' | 'year') =>
    prices
      .filter((p) => p.packageId === pkgId && p.interval === iv && p.status === 'active')
      .reduce<number | null>((min, p) => (min === null || p.amount < min ? p.amount : min), null);
  return ownCatalogPackages(packages, businessResellerId)
    .map((p) => ({ id: p.id, name: p.name, caption: p.tagline ?? null, monthlyMinor: active(p.id, 'month'), yearlyMinor: active(p.id, 'year') }))
    .filter((p) => p.monthlyMinor !== null || p.yearlyMinor !== null);
}

/** Reseller surfaces: the reseller's own published packages (the API returns 0 for "not sold"). */
export function resellerCashPlanChoices<
  P extends { id: string; name: string; status: string; tagline?: string; monthlyMinor: number; yearlyMinor: number },
>(packages: readonly P[]): CashPlanChoice[] {
  return packages
    .filter((p) => p.status === 'published')
    .map((p) => ({
      id: p.id, name: p.name, caption: p.tagline ?? null,
      monthlyMinor: p.monthlyMinor > 0 ? p.monthlyMinor : null,
      yearlyMinor: p.yearlyMinor > 0 ? p.yearlyMinor : null,
    }))
    .filter((p) => p.monthlyMinor !== null || p.yearlyMinor !== null);
}
